import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { fetchAuthSession, signInWithRedirect, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { StorageService, StorageType } from 'src/app/shared/services/storage.service';
import { environment } from 'src/environments/environment';
import { AuthState, AuthToken } from '../state/auth.state';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private authState: AuthState,
    private storageService: StorageService
  ) {
    this._listenToAuthState();
    this.validateAuthentication();
  }

  login(): void {
    signInWithRedirect();
  }

  logout(): void {
    if (this.document.location.search.includes('code=')) {
      return;
    }

    signOut()
      .catch(() => {
        console.error('Error signing out user');
      })
      .finally(() => {
        sessionStorage.clear();
        this.storageService.clearAll(StorageType.LOCAL_STORAGE);

        this.authState.isAuthenticated = false;
        this.authState.authToken = null;

        this._redirectToLogin();
      });
  }

  async getAuthToken(): Promise<AuthToken | null> {
    try {
      const res = await fetchAuthSession();

      if (!res.tokens?.idToken || !res.tokens?.accessToken) {
        throw new Error('Missing tokens');
      }

      return {
        idToken: res.tokens.idToken.toString(),
        accessToken: res.tokens.accessToken.toString(),
      };
    } catch (error) {
      console.error('Error fetching auth token:', error);

      if (!sessionStorage.getItem('redirectInitiated')) {
        sessionStorage.setItem('redirectInitiated', 'true');

        this._redirectToLogin();
      }

      return null;
    }
  }

  async validateAuthentication(): Promise<boolean> {
    return await this.getAuthToken().then((res) => {
      if (res) {
        this.authState.isAuthenticated = true;
        this.authState.authToken = {
          idToken: res?.idToken ?? null,
          accessToken: res?.accessToken ?? null,
        };

        return true;
      } else {
        this.authState.isAuthenticated = false;
        this.authState.authToken = null;
        return false;
      }
    });
  }

  /** Listens to authentication events and update authentication state */
  private _listenToAuthState() {
    Hub.listen('auth', ({ payload: { event } }) => {
      switch (event) {
        case 'signedIn':
        case 'signInWithRedirect':
          this.authState.isAuthenticated = true;
          break;

        case 'signedOut':
        case 'signInWithRedirect_failure':
          this.authState.isAuthenticated = false;
          break;
      }
    });
  }

  private _redirectToLogin(): void {
    if (this.document.location.pathname.includes('/login') || this.document.location.search.includes('code=')) {
      return;
    }

    window.location.replace(this._getLogInUrl());
  }

  private _getLogInUrl(): string {
    const logInUrl = new URL(environment.logInUrl);
    const currentUrl = new URL(this.document.location.href);

    if (currentUrl.pathname != '/not-found') {
      const redirectUrl = currentUrl.toString();

      logInUrl.searchParams.set('redirectUrl', redirectUrl);
    }

    return logInUrl.toString();
  }
}
